import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { setAuthToken, setRefreshToken } from '../../utils/token';

// Simple JWT decoder function
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

interface FormData {
  dateHired: string;
  wageScale: string;
  firstWeekOfWork: string;
  notes: string;
}

interface ApplicationDetails {
  id: string;
  firstName: string;
  lastName: string;
  jobAppliedFor: string;
  dateHired?: string;
  wageScale?: string;
  firstWeekOfWork?: string;
  notes?: string;
  status?: number;
}

const ApplicationAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    dateHired: '',
    wageScale: '',
    firstWeekOfWork: '',
    notes: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const token = searchParams.get('token');
  const action = searchParams.get('action');
  const isApproval = action?.toLowerCase() === 'approve';

  // Fetch application details on component mount
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (!token) {
        Swal.fire('Error', 'Token not provided in the query string.', 'error');
        setLoading(false);
        return;
      }

      try {
        // Decode JWT token to extract applicationId
        const decodedToken = decodeJWT(token);
        if (!decodedToken || !decodedToken.applicationId) {
          Swal.fire('Error', 'Invalid token or applicationId not found in token.', 'error');
          setLoading(false);
          return;
        }

        const applicationId = decodedToken.applicationId;

        const response = await axios.get(`${API_BASE_URL}/JobApplication/${applicationId}`);

        if (response.status === 200 && response.data) {
          const details = response.data;
          setApplicationDetails(details);
          
          // Helper function to format date for HTML input
          const formatDateForInput = (dateString: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return '';
              return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
            } catch {
              return '';
            }
          };

          // Helper function to format week for HTML input
          const formatWeekForInput = (weekString: string) => {
            if (!weekString) return '';
            // If it's already in YYYY-Www format, return as is
            if (weekString.match(/^\d{4}-W\d{2}$/)) return weekString;
            // Try to convert from date to week format
            try {
              const date = new Date(weekString);
              if (isNaN(date.getTime())) return '';
              const year = date.getFullYear();
              const weekNumber = getWeekNumber(date);
              return `${year}-W${weekNumber.toString().padStart(2, '0')}`;
            } catch {
              return '';
            }
          };

          // Helper function to get week number
          const getWeekNumber = (date: Date) => {
            const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
            const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
            return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
          };
          
          // Prefill form with existing data if available
          setFormData({
            dateHired: formatDateForInput(details.dateHired),
            wageScale: details.wageScale || '',
            firstWeekOfWork: formatWeekForInput(details.firstWeekOfWork),
            notes: details.notes || ''
          });
        }
      } catch (error: any) {
        if (axios.isAxiosError(error)) {
          const backendMessage = error.response?.data?.message || 'Failed to fetch application details.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: backendMessage,
          });
        } else {
          Swal.fire('Error', 'An unexpected error occurred while fetching application details.', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchApplicationDetails();
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Notes are always required (for both approval and rejection)
    if (!formData.notes.trim()) {
      newErrors.notes = isApproval 
        ? 'Notes are required for approval'
        : 'Reason for rejection is required';
    }

    if (isApproval) {
      if (!formData.dateHired) {
        newErrors.dateHired = 'Date Hired is required for approval';
      }
      if (!formData.wageScale.trim()) {
        newErrors.wageScale = 'Wage Scale is required for approval';
      }
      if (!formData.firstWeekOfWork) {
        newErrors.firstWeekOfWork = 'First Week of Work is required for approval';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAction = async () => {
    if (!token || !action) {
      Swal.fire('Error', 'Token or action not provided in the query string.', 'error');
      return;
    }

    // Validate form for both approval and rejection
    if (!validateForm()) {
      return;
    }

    let status;
    if (action.toLowerCase() === 'approve') {
      status = 0;
    } else if (action.toLowerCase() === 'reject') {
      status = 1;
    } else {
      Swal.fire('Error', 'Invalid action.', 'error');
      return;
    }

    try {
      const requestData: any = {
        token,
        status,
        notes: formData.notes
      };

      // Only include hiring fields for approval
      if (isApproval) {
        requestData.dateHired = formData.dateHired;
        requestData.wageScale = formData.wageScale;
        requestData.firstWeekOfWork = formData.firstWeekOfWork;
      }

      const response = await axios.post(`${API_BASE_URL}/JobApplication/action`, requestData);

      if (response.status === 200) {
        const { token, refreshToken } = response.data;
        setAuthToken(token);
        setRefreshToken(refreshToken);
        Swal.fire('Success', 'Action completed successfully!', 'success').then(() => {
          navigate('/applications');
        });
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const backendMessage = error.response?.data?.message || 'An unknown error occurred.';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: backendMessage,
          footer: `<pre>${error.response?.data?.details}</pre>`,
        });
      } else {
        Swal.fire('Error', 'An unexpected error occurred.', 'error');
      }
    }
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-md-10">
          <div className="card shadow-lg border-0">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title mb-0">
                <i className={`fa ${isApproval ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                Application {isApproval ? 'Approval' : 'Rejection'}
              </h3>
            </div>
            <div className="card-body p-4">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading application details...</p>
                </div>
              ) : (
                <>
                  {applicationDetails && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="alert alert-info border-0 shadow-sm">
                          <div className="d-flex align-items-center mb-3">
                            <i className="fa fa-user me-2 text-primary fs-5"></i>
                            <h6 className="alert-heading mb-0 fw-bold">Application Details</h6>
                          </div>
                          <div className="row g-3">
                            <div className="col-12">
                              <div className="row">
                                <div className="col-md-8">
                                  <div className="d-flex align-items-center">
                                    <strong className="text-muted me-3" style={{ minWidth: '90px', flexShrink: 0 }}>Applicant:</strong>
                                    <span className="text-dark fw-medium text-nowrap">{`${applicationDetails.firstName} ${applicationDetails.lastName}`}</span>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="d-flex align-items-center">
                                    <strong className="text-muted me-3" style={{ minWidth: '80px', flexShrink: 0 }}>Position:</strong>
                                    <span className="text-dark fw-medium">{applicationDetails.jobAppliedFor}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="row">
                    <div className="col-12 mb-4">
                      <div className={`alert ${isApproval ? 'alert-success' : 'alert-warning'} alert-dismissible`}>
                        <h5 className="alert-heading">
                          <i className={`fa ${isApproval ? 'fa-info-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                          {isApproval ? 'Approval Information' : 'Rejection Notice'}
                        </h5>
                        <p className="mb-0">
                          {isApproval 
                            ? 'Please fill in the required information below to approve this application.'
                            : 'You are about to reject this application. Please provide a reason in the notes field below.'}
                        </p>
                      </div>
                    </div>

                    {isApproval && (
                      <>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="dateHired" className="form-label fw-bold">
                            <i className="fa fa-calendar me-2"></i>Date Hired <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            id="dateHired"
                            name="dateHired"
                            className={`form-control ${errors.dateHired ? 'is-invalid' : ''}`}
                            value={formData.dateHired}
                            onChange={handleInputChange}
                          />
                          {errors.dateHired && <div className="invalid-feedback">{errors.dateHired}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label htmlFor="wageScale" className="form-label fw-bold">
                            <i className="fa fa-dollar-sign me-2"></i>Wage Scale <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="wageScale"
                            name="wageScale"
                            className={`form-control ${errors.wageScale ? 'is-invalid' : ''}`}
                            value={formData.wageScale}
                            onChange={handleInputChange}
                            placeholder="Wage scale.."
                            maxLength={100}
                          />
                          {errors.wageScale && <div className="invalid-feedback">{errors.wageScale}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label htmlFor="firstWeekOfWork" className="form-label fw-bold">
                            <i className="fa fa-calendar-week me-2"></i>First Week of Work <span className="text-danger">*</span>
                          </label>
                          <input
                            type="week"
                            id="firstWeekOfWork"
                            name="firstWeekOfWork"
                            className={`form-control ${errors.firstWeekOfWork ? 'is-invalid' : ''}`}
                            value={formData.firstWeekOfWork}
                            onChange={handleInputChange}
                            placeholder="Select week (e.g., 2024-W01)"
                          />
                          {errors.firstWeekOfWork && <div className="invalid-feedback">{errors.firstWeekOfWork}</div>}
                        </div>
                      </>
                    )}

                    <div className="col-12 mb-3">
                      <label htmlFor="notes" className="form-label fw-bold">
                        <i className="fa fa-sticky-note me-2"></i>
                        {isApproval ? 'Notes' : 'Reason for Rejection'} <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        className={`form-control ${errors.notes ? 'is-invalid' : ''}`}
                        rows={4}
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder={isApproval 
                          ? "Enter detailed notes about the approval..."
                          : "Please provide a detailed reason for rejecting this application..."
                        }
                        maxLength={2000}
                      />
                      {errors.notes && <div className="invalid-feedback">{errors.notes}</div>}
                      <div className="form-text">
                        {formData.notes.length}/2000 characters
                      </div>
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-12 text-center">
                      <button 
                        className={`btn ${isApproval ? 'btn-success' : 'btn-warning'} btn-lg px-5`}
                        onClick={handleAction}
                      >
                        <i className={`fa ${isApproval ? 'fa-check' : 'fa-times'} me-2`}></i>
                        {isApproval ? 'Approve Application' : 'Reject Application'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationAction;
